/**
 * @fileoverview Plans & Documents Section Component
 *
 * Displays project plans and documents organized by tabs:
 * - Floorplans
 * - Site Plans
 * - Other Documents
 *
 * Features:
 * - Tab navigation for different document types
 * - Document cards with thumbnails
 * - Click to view full document
 * - Upload button to add new documents
 *
 * @version 1.0.0
 */

import React, { useState } from "react";
import {
  FaFileAlt,
  FaUpload,
  FaFilePdf,
  FaImage,
  FaExternalLinkAlt,
  FaTrash,
} from "react-icons/fa";
import * as Sentry from "@sentry/react";
import { functions } from "../../../../firebase";
import { httpsCallable } from "firebase/functions";
import PlansDocsUploadModal from "../../../../components/admin/PlansDocsUploadModal";
import DocumentViewerModal from "../../../../components/admin/DocumentViewerModal";
import ConfirmationModal from "../../../../components/ConfirmationModal";

/**
 * Plans tab options
 * @constant {Array<Object>}
 */
const PLAN_TABS = [
  { value: "floorplans", label: "Floorplans" },
  { value: "siteplans", label: "Site Plans" },
  { value: "other", label: "Other Documents" },
];

/**
 * Section wrapper component with consistent styling
 */
const Section = ({ title, icon, children, action }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/**
 * Tab navigation component
 */
const TabNav = ({ tabs, activeTab, onTabChange }) => (
  <div className="apd-tabs">
    {tabs.map((tab) => (
      <button
        key={tab.value}
        className={`apd-tab ${activeTab === tab.value ? "active" : ""}`}
        onClick={() => onTabChange(tab.value)}
      >
        {tab.label}
        {tab.count !== undefined && (
          <span className="apd-tab-count">{tab.count}</span>
        )}
      </button>
    ))}
  </div>
);

/**
 * Document/Plan card component with thumbnail preview
 */
const DocumentCard = ({ doc, onClick, onDelete }) => (
  <div className="apd-doc-card" onClick={() => onClick(doc)}>
    <div className="apd-doc-thumbnail">
      {doc.thumbnail ? (
        <img src={doc.thumbnail} alt={doc.name} />
      ) : (
        <div className="apd-doc-icon">
          {doc.type === "pdf" ? <FaFilePdf /> : <FaImage />}
        </div>
      )}
      <div className="apd-doc-overlay">
        <FaExternalLinkAlt />
      </div>
    </div>
    <div className="apd-doc-card-footer">
      <span className="apd-doc-name">{doc.name}</span>
      {onDelete && (
        <button
          className="apd-doc-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(doc);
          }}
          aria-label={`Delete ${doc.name}`}
        >
          <FaTrash />
        </button>
      )}
    </div>
  </div>
);

/**
 * PlansDocuments Component
 * Displays project plans and documents with tab navigation
 *
 * @param {Object} props - Component props
 * @param {string} props.projectId - The project ID
 * @param {string} props.projectName - The project name (for upload modal)
 * @param {Object} props.plans - Plans object with { floorplans: [], siteplans: [], other: [] }
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.activePlanTab - Currently active tab
 * @param {Function} props.setActivePlanTab - Function to change active tab
 * @param {Function} props.onUploadComplete - Callback after successful upload
 */
const PlansDocuments = ({
  projectId,
  projectName,
  plans,
  isLoading,
  activePlanTab,
  setActivePlanTab,
  onUploadComplete,
}) => {
  // Local state for modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Organize plans by category
   * Plans come as an object: { floorplans: [], siteplans: [], other: [] }
   */
  const organizedPlans = plans || { floorplans: [], siteplans: [], other: [] };

  /**
   * Handle document click to open viewer
   */
  const handleDocClick = (doc) => {
    setSelectedDocument(doc);
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const deleteAttachment = httpsCallable(
        functions,
        "deleteProjectAttachment",
      );
      await deleteAttachment({
        projectId,
        field: activePlanTab,
        attachmentId: deleteTarget.id,
      });
      setDeleteTarget(null);
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error("[PlansDocuments] Delete error:", error);
      Sentry.captureException(error);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle upload modal close
   */
  const handleUploadModalClose = () => {
    setShowUploadModal(false);
  };

  /**
   * Handle upload complete
   */
  const handleUploadComplete = () => {
    setShowUploadModal(false);
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  if (isLoading) {
    return (
      <Section title="Plans & Documents" icon={<FaFileAlt />}>
        <div className="apd-loading-small">
          <div className="apd-spinner-small"></div>
          <p>Loading plans...</p>
        </div>
      </Section>
    );
  }

  return (
    <>
      <Section
        title="Plans & Documents"
        icon={<FaFileAlt />}
        action={
          <button
            className="apd-add-btn"
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload /> <span className="hide-sm">Upload</span>
          </button>
        }
      >
        <TabNav
          tabs={PLAN_TABS}
          activeTab={activePlanTab}
          onTabChange={setActivePlanTab}
        />
        <div className="apd-docs-grid">
          {organizedPlans[activePlanTab]?.length > 0 ? (
            organizedPlans[activePlanTab].map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                onClick={handleDocClick}
                onDelete={setDeleteTarget}
              />
            ))
          ) : (
            <p className="apd-empty-message">No documents in this category</p>
          )}
        </div>
      </Section>

      {/* Upload Modal */}
      {showUploadModal && (
        <PlansDocsUploadModal
          isOpen={showUploadModal}
          onClose={handleUploadModalClose}
          projectId={projectId}
          projectName={projectName}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewerModal
          doc={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Document?"
        message={`Are you sure you want to delete`}
        item={`${deleteTarget?.name}`}
        punc={"?"}
        warning="This will permanently remove this document from the project."
        proceed="This action cannot be undone."
        confirmText="Delete"
        isDestructive
        isLoading={isDeleting}
      />
    </>
  );
};

export default PlansDocuments;
